import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { IComponentName, IFilterParams, IParamsList } from '../../interfaces';
import {
    useIsLocalFilter,
    usePagesStoreCurrentComponent,
    useRouterStore,
    useUiLastViewComponentName,
    useUiViewInfo,
} from '../../hooks';
import {
    abortAllFetch,
    getDefaultPageParams,
    setRouterStoreUseExternalUrlManager,
    setUiForcedUpdateTime,
    setUiInitPageList,
    setUiLastViewComponentName,
    setUiUrlStorePage,
} from '../../middleware';
import { equalObjects, queryParamsCleanDefault, queryParamsNormalize, urlParamsCleaner } from '../../instruments';

interface IUrlManagerProps {
    cleanStore(): void;
    defaultParams?: IParamsList;
    filterParams?: IFilterParams;
    getRelationSelect?(): void;
    setForcedUpdateTime?(): void;
}

export const FilterUrlManager: React.FC<IUrlManagerProps> = observer(
    ({ cleanStore, defaultParams, filterParams, getRelationSelect, setForcedUpdateTime = setUiForcedUpdateTime }) => {
        const [filterCurrentComponentName, setFilterCurrentComponentName] = useState<IComponentName>();
        const routerStore = useRouterStore();
        const lastViewComponentName = useUiLastViewComponentName();
        const { queryParams } = routerStore.routerState;
        const requestPage = usePagesStoreCurrentComponent();
        const { routeName } = requestPage;
        const cleanQueryParams = urlParamsCleaner({ ...queryParams, routeName }, filterParams);
        const normalizeCleanQueryParams = queryParamsNormalize(cleanQueryParams, filterParams);
        const defaultPageParams = getDefaultPageParams(routeName);
        const storeParams = useUiViewInfo();
        const cleanStoreParams = urlParamsCleaner(storeParams, filterParams);
        const isLocalFilter = useIsLocalFilter();
        const updateFirstStart = (): void => {
            let newParams: IParamsList = urlParamsCleaner(
                {
                    ...defaultPageParams,
                },
                filterParams,
            );
            if (routeName === routerStore.routerState.routeName) {
                const cleanFirstQueryParams = urlParamsCleaner(requestPage.queryParams || {}, filterParams);
                const normalizeFirstCleanQueryParams = queryParamsNormalize(cleanFirstQueryParams, filterParams);
                newParams = urlParamsCleaner(
                    {
                        ...defaultPageParams,
                        ...defaultParams,
                        ...normalizeFirstCleanQueryParams,
                    },
                    filterParams,
                );
            }
            setUiUrlStorePage({ ...newParams, routeName });
            const newQueryParams = filterParams?.isDateRangeCleanedDefaultURL
                ? queryParamsCleanDefault(newParams, filterParams)
                : newParams;
            routerStore.goTo(filterCurrentComponentName || routeName, { queryParams: newQueryParams });
            setForcedUpdateTime();
        };

        function updatePageUrl(): void {
            if (filterCurrentComponentName && filterCurrentComponentName === routerStore.routerState.routeName) {
                const newParams: IParamsList = urlParamsCleaner(
                    {
                        ...defaultPageParams,
                        ...storeParams,
                    },
                    filterParams,
                );
                if (!equalObjects(newParams, normalizeCleanQueryParams)) {
                    const cleanQueryParams = urlParamsCleaner(newParams, filterParams);
                    const newUrlParams = filterParams?.isDateRangeCleanedDefaultURL
                        ? queryParamsCleanDefault(cleanQueryParams, filterParams)
                        : cleanQueryParams;
                    routerStore.goTo(filterCurrentComponentName || routeName, { queryParams: newUrlParams });
                }
            }
        }
        function updatePageStore(): void {
            if (filterCurrentComponentName && filterCurrentComponentName === routerStore.routerState.routeName) {
                const newParams: IParamsList = urlParamsCleaner(
                    {
                        ...defaultPageParams,
                        ...normalizeCleanQueryParams,
                    },
                    filterParams,
                );
                if (!equalObjects(newParams, cleanStoreParams)) {
                    setUiUrlStorePage({ ...newParams, routeName });
                    setForcedUpdateTime();
                }
            }
        }

        useEffect(() => {
            setRouterStoreUseExternalUrlManager(true);
            setFilterCurrentComponentName(routeName);
            // if (false && routeName !== lastViewComponentName) {
            if (routeName !== lastViewComponentName) {
                setUiInitPageList();
                setUiLastViewComponentName(routeName);
                updateFirstStart();
            } else {
                setForcedUpdateTime();
            }
            getRelationSelect && getRelationSelect();
            return (): void => {
                abortAllFetch();
                cleanStore();
                setRouterStoreUseExternalUrlManager(false);
            };
        }, []);

        useEffect(() => {
            updatePageStore();
        }, [JSON.stringify(normalizeCleanQueryParams)]);

        useEffect(() => {
            if (!isLocalFilter) {
                updatePageUrl();
            }
        }, [JSON.stringify(cleanStoreParams)]);

        return null;
    },
);
